// client/src/components/unidades/UnidadeFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useUnidadesPorConta, useMutateUnidades, useUnidade } from '../../hooks/useUnidades';
import { Loader2, XCircle, MapPin } from 'lucide-react';
import useAlert from '../../hooks/useAlert';
import UnidadeForm from './UnidadeForm';
import PageHeader from '../layout/PageHeader';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_LABELS = ['Informações da Unidade', 'Configurações de Agenda'];

const UnidadeFormPage = () => {
    const { unidadeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setAlert } = useAlert();
    
    const isEditing = !!unidadeId;
    const { contaId } = location.state || {};

    // ✅ Buscar unidade específica se estiver editando
    const { data: unidadeData, isLoading: isLoadingUnidade } = useUnidade(unidadeId, {
        enabled: isEditing && !!unidadeId
    });
    
    // ✅ Mutações
    const { createUnidade, updateUnidade } = useMutateUnidades();

    const [currentStep, setCurrentStep] = useState(1);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        nome: '',
        endereco: {
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            localidade: '',
            uf: '',
            pontoReferencia: ''
        },
        contato: {
            telefone: '',
            telefone2: '',
            whatsapp: '',
            email: '',
            email2: '',
            site: '',
            redesSociais: {}
        },
        codigo: '',
        ativo: true,
        configuracoesAgenda: {
            intervaloGradeHorarios: 60,
            permiteAgendamentoOnline: true,
            diasAntecedenciaMaxima: 30
        },
        metadata: {
            capacidade: 0,
            possuiEstacionamento: false,
            acessibilidade: false,
            possuiWifi: true
        }
    });

    // ✅ Carregar dados da unidade quando em modo edição
    useEffect(() => {
        if (isEditing && unidadeData?.data) {
            const unidade = unidadeData.data;
            setFormData({
                nome: unidade.nome || '',
                endereco: {
                    cep: unidade.endereco?.cep || '',
                    logradouro: unidade.endereco?.logradouro || '',
                    numero: unidade.endereco?.numero || '',
                    complemento: unidade.endereco?.complemento || '',
                    bairro: unidade.endereco?.bairro || '',
                    localidade: unidade.endereco?.localidade || '',
                    uf: unidade.endereco?.uf || '',
                    pontoReferencia: unidade.endereco?.pontoReferencia || ''
                },
                contato: {
                    telefone: unidade.contato?.telefone || '',
                    telefone2: unidade.contato?.telefone2 || '',
                    whatsapp: unidade.contato?.whatsapp || '',
                    email: unidade.contato?.email || '',
                    email2: unidade.contato?.email2 || '',
                    site: unidade.contato?.site || '',
                    redesSociais: unidade.contato?.redesSociais || {}
                },
                codigo: unidade.codigo || '',
                ativo: unidade.ativo !== false,
                configuracoesAgenda: {
                    intervaloGradeHorarios: unidade.configuracoesAgenda?.intervaloGradeHorarios || 60,
                    permiteAgendamentoOnline: unidade.configuracoesAgenda?.permiteAgendamentoOnline !== false,
                    diasAntecedenciaMaxima: unidade.configuracoesAgenda?.diasAntecedenciaMaxima || 30
                },
                metadata: {
                    capacidade: unidade.metadata?.capacidade || 0,
                    possuiEstacionamento: unidade.metadata?.possuiEstacionamento || false,
                    acessibilidade: unidade.metadata?.acessibilidade || false,
                    possuiWifi: unidade.metadata?.possuiWifi !== false
                }
            });
        }
    }, [isEditing, unidadeData]);

    const formatTelefone = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 2) return numbers;
        if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    };

    const formatCEP = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 5) return numbers;
        return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
    };

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        
        let formattedValue = value;
        if (name.includes('telefone') || name.includes('whatsapp')) {
            formattedValue = formatTelefone(value);
        } else if (name.includes('cep')) {
            formattedValue = formatCEP(value);
        }
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: formattedValue
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: formattedValue }));
        }
    }, []);

    const handleBuscarCEP = useCallback(async () => {
        const cep = (formData.endereco.cep || '').replace(/\D/g, '');
        if (cep.length !== 8) {
            setAlert('CEP deve conter 8 dígitos', 'warning');
            return;
        }

        setIsPageLoading(true);
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();
            
            if (data.erro) {
                setAlert('CEP não encontrado', 'danger');
                return;
            }

            setFormData(prev => ({
                ...prev,
                endereco: {
                    ...prev.endereco,
                    logradouro: data.logradouro || '',
                    bairro: data.bairro || '',
                    localidade: data.localidade || '',
                    uf: data.uf || ''
                }
            }));
            setAlert('Endereço preenchido automaticamente!', 'success');
        } catch (error) {
            setAlert('Erro ao buscar CEP', 'danger');
        } finally {
            setIsPageLoading(false);
        }
    }, [formData.endereco.cep, setAlert]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validações básicas
        if (!formData.nome.trim()) {
            setAlert('Nome da unidade é obrigatório', 'warning');
            return;
        }
        
        if (!formData.endereco.localidade.trim() || !formData.endereco.uf.trim()) {
            setAlert('Localidade e UF são obrigatórios', 'warning');
            return;
        }

        if (!contaId && !isEditing) {
            setAlert('ID da conta não encontrado', 'danger');
            return;
        }

        setIsSaving(true);

        try {
            // Preparar dados para envio
            const dataToSend = {
                nome: formData.nome.trim(),
                endereco: {
                    ...formData.endereco,
                    cep: formData.endereco.cep ? formData.endereco.cep.replace(/\D/g, '') : '',
                    uf: formData.endereco.uf?.toUpperCase()
                },
                contato: {
                    ...formData.contato,
                    telefone: formData.contato.telefone ? formData.contato.telefone.replace(/\D/g, '') : '',
                    telefone2: formData.contato.telefone2 ? formData.contato.telefone2.replace(/\D/g, '') : '',
                    whatsapp: formData.contato.whatsapp ? formData.contato.whatsapp.replace(/\D/g, '') : ''
                },
                codigo: formData.codigo || undefined,
                ativo: formData.ativo,
                configuracoesAgenda: formData.configuracoesAgenda,
                metadata: formData.metadata
            };

            console.log('📤 Salvando unidade:', dataToSend);
            
            if (isEditing) {
                await updateUnidade.mutateAsync({
                    unidadeId: unidadeId,
                    unidadeData: dataToSend
                });
                setAlert('Unidade atualizada com sucesso!', 'success');
            } else {
                await createUnidade.mutateAsync({
                    accountId: contaId,
                    unidadeData: dataToSend
                });
                setAlert('Unidade criada com sucesso!', 'success');
            }
            
            navigate('/minha-conta/unidades');
            
        } catch (error) {
            console.error('❌ Erro ao salvar unidade:', error);
            setAlert(error.response?.data?.error || 'Erro ao salvar unidade. Tente novamente.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const isLoadingData = isLoadingUnidade || (isEditing && !formData.nome);

    if (isLoadingData) {
        return (
            <div className="page-management-container">
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="animate-spin text-blue-600" size={40} />
                </div>
            </div>
        );
    }

    return (
        <div className="page-management-container">
            <PageHeader
                icon={<MapPin className="icon-title" />}
                title={isEditing ? `Editar: ${formData.nome}` : 'Nova Unidade'}
                actions={
                    <button
                        type="button"
                        onClick={() => navigate('/minha-conta/unidades')}
                        className="action-button action-button-secondary"
                    >
                        <XCircle size={18} className="icon" /> Cancelar
                    </button>
                }
            />

            <form id="unidade-form" onSubmit={handleSubmit}>
                <div className="content-panel">
                    <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

                    {currentStep === 1 && (
                        <UnidadeForm
                            formData={formData}
                            onChange={handleChange}
                            onCepChange={handleBuscarCEP}
                            isEditing={true}
                            isCreating={!isEditing}
                            errors={{}}
                        />
                    )}

                    {currentStep === 2 && (
                        <fieldset className="form-section" disabled={isSaving}>
                            <legend className="form-section-title">Configurações da Agenda</legend>
                            <div className="form-grid-container">
                                <div className="form-group">
                                    <label>Intervalo da Grade</label>
                                    <select
                                        name="configuracoesAgenda.intervaloGradeHorarios"
                                        value={formData.configuracoesAgenda.intervaloGradeHorarios}
                                        onChange={handleChange}
                                        className="form-input"
                                    >
                                        <option value="15">15 minutos</option>
                                        <option value="30">30 minutos</option>
                                        <option value="45">45 minutos</option>
                                        <option value="60">60 minutos</option>
                                        <option value="120">2 horas</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Dias de Antecedência</label>
                                    <input
                                        type="number"
                                        name="configuracoesAgenda.diasAntecedenciaMaxima"
                                        value={formData.configuracoesAgenda.diasAntecedenciaMaxima}
                                        onChange={handleChange}
                                        className="form-input"
                                        min="1"
                                        max="90"
                                    />
                                </div>
                                <div className="form-group full-width">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="configuracoesAgenda.permiteAgendamentoOnline"
                                            checked={formData.configuracoesAgenda.permiteAgendamentoOnline}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                configuracoesAgenda: {
                                                    ...prev.configuracoesAgenda,
                                                    permiteAgendamentoOnline: e.target.checked
                                                }
                                            }))}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-label-text">Permitir agendamento online</span>
                                    </label>
                                </div>
                            </div>
                        </fieldset>
                    )}

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/minha-conta/unidades')}
                            className="action-button action-button-secondary"
                        >
                            <XCircle size={18} className="icon" /> Cancelar
                        </button>
                        <FormStepNavigation
                            currentStep={currentStep}
                            totalSteps={STEP_LABELS.length}
                            onBack={() => setCurrentStep(s => s - 1)}
                            onNext={() => setCurrentStep(s => s + 1)}
                            isSubmitting={isSaving}
                            submitLabel={isEditing ? 'Salvar Alterações' : 'Criar Unidade'}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default UnidadeFormPage;